// bestFitDiameterUtils.js

/**
 * fitCircleKasa:
 *   Fits a circle (center and radius) to an array of 2D points using the Kasa method.
 *   Points should be in the form: [{ x: Number, y: Number }, ...]
 *   Returns an object: { center: { x, y }, radius } or null if fitting fails.
 */
export function fitCircleKasa(points) {
  const n = points.length;
  if (n < 3) return null; // Need at least 3 points to define a circle.

  let sumX = 0,
    sumY = 0,
    sumX2 = 0,
    sumY2 = 0,
    sumXY = 0,
    sumX3 = 0,
    sumY3 = 0,
    sumX2Y = 0,
    sumXY2 = 0;

  for (let i = 0; i < n; i++) {
    const { x, y } = points[i];
    const x2 = x * x;
    const y2 = y * y;
    sumX += x;
    sumY += y;
    sumX2 += x2;
    sumY2 += y2;
    sumXY += x * y;
    sumX3 += x2 * x;
    sumY3 += y2 * y;
    sumX2Y += x2 * y;
    sumXY2 += x * y2;
  }

  const C = n * sumX2 - sumX * sumX;
  const D = n * sumXY - sumX * sumY;
  const E = n * sumY2 - sumY * sumY;
  const G = 0.5 * (n * sumX3 + n * sumXY2 - sumX * (sumX2 + sumY2));
  const H = 0.5 * (n * sumX2Y + n * sumY3 - sumY * (sumX2 + sumY2));

  const denominator = C * E - D * D;
  if (Math.abs(denominator) < 1e-12) return null;

  const a = (G * E - D * H) / denominator;
  const b = (C * H - D * G) / denominator;

  // Compute the average distance (radius) from the fitted center.
  let sumR = 0;
  for (let i = 0; i < n; i++) {
    const dx = points[i].x - a;
    const dy = points[i].y - b;
    sumR += Math.sqrt(dx * dx + dy * dy);
  }
  const radius = sumR / n;
  return { center: { x: a, y: b }, radius };
}

/**
 * getBestFitDiameter:
 *   Computes the best-fit diameter from an array of 2D points using a least-squares circle fit.
 *   Returns the diameter (2 * radius) or null if the fitting fails.
 */
export function getBestFitDiameter(points) {
  const circle = fitCircleKasa(points);
  if (!circle) return null;
  return circle.radius * 2;
}
